import React from "react"

import Button from "@src/components/Button"
import GhostButton from "@src/components/Buttons/GhostButton"
import AccountConfirmModal, {
  ConfirmModalProps,
} from "@src/components/AccountConfirmModal"
import ProductQuantitySelect from "@src/components/product-quantity-select"
import RitualFrequencySelect from "@src/components/ritual-frequency-select"

import { formatCurrency } from "@src/utilities/format-numbers"

import type {
  GetMyUserRecordQuery,
  RemoveFromRitualPlusMutationVariables,
  SkipRitualPlusDeliveryMutationVariables,
  UpdateRitualPlusFrequencyMutationVariables,
  UpdateRitualPlusQuantityMutationVariables,
} from "@src/entities/types/api"

export interface AccountRitualPlusProps {
  onReactivateSubscription: () => Promise<void>
  onRemoveItem: (
    input: RemoveFromRitualPlusMutationVariables["input"]
  ) => Promise<void>
  onSkipNextDelivery: (
    input: SkipRitualPlusDeliveryMutationVariables["input"]
  ) => Promise<void>
  onUpdateFrequency: (
    input: UpdateRitualPlusFrequencyMutationVariables["input"]
  ) => Promise<void>
  onUpdateQuantity: (
    input: UpdateRitualPlusQuantityMutationVariables["input"]
  ) => Promise<void>
  subscription: GetMyUserRecordQuery["myRitualPlus"]
}
export default function AccountRitualPlus({
  onReactivateSubscription,
  onRemoveItem,
  onSkipNextDelivery,
  onUpdateFrequency,
  onUpdateQuantity,
  subscription,
}: AccountRitualPlusProps) {
  const [isEditing, setIsEditing] = React.useState<boolean>(false)
  const [modal, setModal] = React.useState<Pick<
    ConfirmModalProps,
    "confirmButtonText" | "isProcessing" | "message" | "onConfirm" | "title"
  > | null>(null)

  const formattedDate = (dateString: string) =>
    new Intl.DateTimeFormat("en-US").format(new Date(dateString))

  React.useEffect(() => {
    if (!isEditing) {
      setModal(null)
    }
  }, [isEditing])

  return (
    <section className="mb-8">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xl font-extrabold font-browser">
          My Ritual+ Subscription
        </h3>
        {subscription && (
          <GhostButton
            onClick={() => setIsEditing((prevState) => !prevState)}
            text={isEditing ? "Cancel" : "Edit"}
            size="small"
          />
        )}
      </div>
      {!subscription && (
        <div>
          <p>
            Your Ritual+ subscription will appear here. You do not currently
            have any subscriptions.
          </p>
        </div>
      )}
      {subscription && (
        <>
          {!subscription.active && (
            <div className="border border-red bg-red/10 text-red text-sm p-4 mb-4">
              Your subscription is not currently active. This may be an issue
              with your billing. Please update your card below or{" "}
              <Button
                onClick={() =>
                  setModal({
                    confirmButtonText: `Yes, reactivate it`,
                    isProcessing: false,
                    message: `Do you want to reactivate your Ritual+?`,
                    onConfirm: () => {
                      setModal((prevState) => ({
                        ...prevState!,
                        isProcessing: true,
                      }))
                      onReactivateSubscription().then(() => setModal(null))
                    },
                    title: "Reactivate Ritual+",
                  })
                }
                text="click here to try to reactivate it"
                type="inlineWarningLink"
              />
              .
            </div>
          )}
          <div>
            {subscription.lineItems.map((lineItem, idx) => (
              <div
                className="flex items-start justify-start"
                key={`${lineItem.product?.sku}_${idx}_${lineItem.frequencyInWeeks}`}
                data-editing-my-ritual-plus={isEditing}
              >
                <div
                  className="bg-center bg-cover block w-24 h-24 mb-2 mr-2"
                  style={{
                    backgroundImage: `url(${
                      lineItem.product?.thumbnailUrl ??
                      "/images/placeholder.jpeg"
                    })`,
                  }}
                />
                <div className="flex-grow">
                  <h4 className="font-browser font-medium">
                    {lineItem.product?.name}
                  </h4>
                  <p className="text-sm">{lineItem.product?.description}</p>
                  {!isEditing && (
                    <p className="text-sm">
                      {lineItem.quantity} unit
                      {lineItem.quantity === 1 ? "" : "s"} every{" "}
                      {lineItem.frequencyInWeeks} weeks, renews on{" "}
                      {formattedDate(lineItem.renewsOn)}
                    </p>
                  )}
                  {isEditing && (
                    <p className="mb-4 text-sm">
                      Renews on {formattedDate(lineItem.renewsOn)}
                      {" - "}
                      <Button
                        onClick={() =>
                          setModal({
                            confirmButtonText: `Yes, skip for ${lineItem.frequencyInWeeks} weeks`,
                            isProcessing: false,
                            message: `Are you sure you want to skip your next delivery? This would skip your next delivery for ${lineItem.frequencyInWeeks} weeks.`,
                            onConfirm: () => {
                              setModal((prevState) => ({
                                ...prevState!,
                                isProcessing: true,
                              }))
                              onSkipNextDelivery({
                                frequencyInWeeks: lineItem.frequencyInWeeks,
                                sku: lineItem.product?.sku!,
                              }).then(() => setIsEditing(false))
                            },
                            title: "Skip Next Delivery",
                          })
                        }
                        text="Skip this delivery"
                        type="inlineLink"
                      />
                    </p>
                  )}
                  {isEditing && (
                    <div className="md:grid md:grid-cols-2 md:gap-4">
                      <div className="mb-2">
                        <div className="mb-1 text-sm block">Quantity</div>
                        <ProductQuantitySelect
                          onChange={(ev) => {
                            const newValue = ev.target.value
                            setModal({
                              confirmButtonText: "Yes, update my quantity",
                              isProcessing: false,
                              message: `Are you sure you want to update the quantity to ${newValue}?`,
                              onConfirm: () => {
                                setModal((prevState) => ({
                                  ...prevState!,
                                  isProcessing: true,
                                }))
                                onUpdateQuantity({
                                  frequencyInWeeks: lineItem.frequencyInWeeks,
                                  newQuantity: Number(newValue),
                                  sku: lineItem.product?.sku!,
                                }).then(() => setIsEditing(false))
                              },
                              title: "Update Quantity",
                            })
                          }}
                          quantity={lineItem.quantity}
                        />
                      </div>
                      <div className="mb-2">
                        <div className="mb-1 text-sm block">Frequency</div>
                        <RitualFrequencySelect
                          onChange={(ev) => {
                            const newValue = ev.target.value
                            setModal({
                              confirmButtonText: "Yes, update my frequency",
                              isProcessing: false,
                              message: `Are you sure you want to change this item to deliver every ${newValue} weeks?`,
                              onConfirm: () => {
                                setModal((prevState) => ({
                                  ...prevState!,
                                  isProcessing: true,
                                }))
                                onUpdateFrequency({
                                  frequencyInWeeks: lineItem.frequencyInWeeks,
                                  newFrequencyInWeeks: Number(newValue),
                                  sku: lineItem.product?.sku!,
                                }).then(() => setIsEditing(false))
                              },
                              title: "Update Delivery Frequency",
                            })
                          }}
                          frequency={lineItem.frequencyInWeeks}
                        />
                      </div>
                    </div>
                  )}
                  {isEditing && (
                    <div className="mt-4">
                      <Button
                        onClick={() => {
                          setModal({
                            confirmButtonText: "Yes, remove this item",
                            isProcessing: false,
                            message: `Are you sure you want to remove this item from your Ritual+?`,
                            onConfirm: () => {
                              setModal((prevState) => ({
                                ...prevState!,
                                isProcessing: true,
                              }))
                              onRemoveItem({
                                frequencyInWeeks: lineItem.frequencyInWeeks,
                                sku: lineItem.product?.sku!,
                              }).then(() => setIsEditing(false))
                            },
                            title: "Remove from Ritual+",
                          })
                        }}
                        text="Remove from my Ritual+"
                        type="inlineLink"
                      />
                    </div>
                  )}
                </div>
                <h4 className="font-browser font-medium">
                  {formatCurrency(lineItem.product?.priceSubscription)}
                </h4>
              </div>
            ))}
          </div>
        </>
      )}
      {modal && (
        <AccountConfirmModal
          confirmButtonText={modal.confirmButtonText}
          message={modal.message}
          isProcessing={modal.isProcessing}
          onCancel={() => setModal(null)}
          onConfirm={modal.onConfirm}
          title={modal.title}
        />
      )}
    </section>
  )
}
